import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { LumeoFile } from 'types/file';
import { RequireAtLeastOne } from 'types/utility_types';
import { UseFilesParams } from 'files/hooks/useFiles';

import { updateQueriesData } from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';
import { UseCheckFilesExistenceResult } from './useCheckFilesExistence';

export type UseDeleteFilesParams = RequireAtLeastOne<UseFilesParams>;

export function useDeleteFiles({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<void, APIError, UseDeleteFilesParams> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation({
    mutationFn: (params) =>
      client.delete(`/apps/${applicationID}/files`, { params }),
    ...options,
    async onMutate(params) {
      await queryClient.cancelQueries(['files']);
      await queryClient.cancelQueries(['file']);
      await queryClient.cancelQueries(['files-existence']);

      onMutate?.(params);

      if ('file_ids' in params && params.file_ids) {
        const filesQueryData = updateQueriesData<LumeoFile>({
          queryClient,
          listQueryKey: ['files'],
          singleQueryKey: ['file'],
          ids: params.file_ids,
          updateData: null,
        });

        const filesExistenceQueriesData =
          queryClient.getQueriesData<UseCheckFilesExistenceResult>([
            'files-existence',
          ]);

        for (const [key, filesExistenceQuery] of filesExistenceQueriesData) {
          if (!filesExistenceQuery) {
            continue;
          }

          const updatedQuery = { ...filesExistenceQuery };

          params.file_ids.forEach((id) => {
            if (id in updatedQuery) {
              updatedQuery[id] = false;
            }
          });

          queryClient.setQueryData(key, updatedQuery);
        }

        return { filesQueryData, filesExistenceQueriesData };
      }
    },
    onError(error, params, context) {
      onError?.(error, params, context);

      context?.filesQueryData.previousLists?.forEach(([queryKey, data]) =>
        queryClient.setQueryData(queryKey, data)
      );
      context?.filesQueryData.previousSingles?.forEach(([queryKey, data]) =>
        queryClient.setQueryData(queryKey, data)
      );

      context?.filesExistenceQueriesData.forEach(([queryKey, data]) =>
        queryClient.setQueryData(queryKey, data)
      );
    },
    onSettled(...args) {
      onSettled?.(...args);
      queryClient.invalidateQueries(['files']);
      queryClient.invalidateQueries(['file']);
      queryClient.invalidateQueries(['files-existence']);
    },
  });
}
